import { useState } from "react";
import axios from "axios";
import { Sparkles, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InlineNewCollection = ({ itemId, onCreated }) => {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const fetchSuggestions = async () => {
    setSuggesting(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/items/${itemId}/suggest-collection-name`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
      );
      setSuggestions(resp.data.suggestions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate suggestions");
    } finally {
      setSuggesting(false);
    }
  };

  const create = async (collectionName) => {
    const finalName = (collectionName || name).trim();
    if (!finalName || creating) return;
    setCreating(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };
      const resp = await axios.post(`${API}/collections`, { name: finalName }, { headers });
      await axios.post(`${API}/collections/${resp.data.id}/items/${itemId}`, {}, { headers });
      toast.success(`Saved in new collection "${finalName}"`);
      setName("");
      setSuggestions([]);
      onCreated?.(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create collection");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="pt-2 space-y-2" data-testid="inline-new-collection">
      <div className="flex gap-1.5">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="New collection name…"
          maxLength={80}
          className="flex-1 h-8 px-2 rounded border border-slate-300 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400"
          data-testid="new-collection-name-input"
        />
        <button
          onClick={() => create()}
          disabled={creating || !name.trim()}
          title="Create and save item"
          className="h-8 px-2 rounded bg-slate-900 text-white disabled:opacity-40 hover:bg-slate-700 transition-colors"
          data-testid="new-collection-create-btn"
        >
          {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        </button>
      </div>
      <button
        onClick={fetchSuggestions}
        disabled={suggesting}
        className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 disabled:opacity-50"
        data-testid="suggest-collection-names-btn"
      >
        {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
        {suggesting ? "Thinking…" : "Suggest names with AI"}
      </button>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => setName(s)}
              className="px-2 py-1 rounded-full bg-orange-50 text-orange-700 text-xs hover:bg-orange-100 transition-colors"
              data-testid={`collection-suggestion-chip`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default InlineNewCollection;
